import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';

import { alertService } from '../../../../shared/services/alertService';
import { usePlanningWizard } from '../PlanningWizardContext';
import { parseDate } from '../dateUtils';

/**
 * Step 1 — Date / Title / Directorate / Counselling Type / Location /
 * Candidate Updated By (Angular counsellingFirstForm + handleDirectorate /
 * handleCounsellingType / handleSlctMngmt / clearToDate / firstStepSubmit).
 */
const StepDetails = () => {
  const {
    detailsForm,
    options,
    locationDisable,
    setLocationDisable,
    userType1,
    setSelectedSchManage,
    nextPage,
    confirmationDetails,
  } = usePlanningWizard();

  const values = detailsForm.values;
  const touched = detailsForm.touched;
  const today = new Date();

  const setField = (name: string, value: unknown) =>
    detailsForm.setFieldValue(name, value);

  // Inline required-error message (Angular p-message severity="error").
  const fieldError = (name: keyof typeof values, text: string) =>
    touched[name] && !values[name] ? (
      <small className="p-error d-block mt-1">{text}</small>
    ) : null;

  // Angular clearToDate: reset ToDate whenever FromDate changes.
  const clearToDate = () => setField('ToDate', null);

  // Angular handleSlctMngmt: preselect management per role / counselling type.
  const handleSlctMngmt = (counsellingType: string, directorate: string) => {
    if (userType1 === '29' && counsellingType !== '9') {
      setSelectedSchManage(['2']);
    } else if (counsellingType !== '4' && directorate !== '5') {
      setSelectedSchManage([
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '9',
        '31',
        '32',
        '36',
      ]);
    } else {
      setSelectedSchManage([]);
    }
  };

  // Angular handleDirectorate: reset dependent fields; schType='1' for GCC/SCERT.
  const handleDirectorate = (value: string) => {
    setField('Directorate', value);
    setField('Location', '');
    setField('CounsellingType', '');
    setField('CandidateUpdatedBy', '');
  };

  // Angular handleCounsellingType: location lock for 5/6/7; candidate-updated-by
  // requirement; management preselect.
  const handleCounsellingType = (value: string) => {
    setField('CounsellingType', value);
    if (value === '5' || value === '6' || value === '7') {
      setField('Location', '1');
      setLocationDisable(true);
    } else {
      setLocationDisable(false);
      setField('Location', '');
    }
    handleSlctMngmt(value, String(values.Directorate));
  };

  const firstStepSubmit = () => {
    const v = detailsForm.values;
    const needsCandidate = v.CounsellingType !== '8';
    if (
      !v.FromDate ||
      !v.ToDate ||
      !v.Title ||
      !v.Directorate ||
      !v.CounsellingType ||
      !v.Location ||
      (needsCandidate && !v.CandidateUpdatedBy)
    ) {
      // Touch all so error messages show.
      detailsForm.setTouched({
        FromDate: true,
        ToDate: true,
        Title: true,
        Directorate: true,
        CounsellingType: true,
        Location: true,
        CandidateUpdatedBy: true,
      });
      alertService.error('Please fill all mandatory fields');
      return;
    }
    confirmationDetails();
    nextPage();
  };

  return (
    <div className="card p-4">
      <div className="row">
        <div className="col">
          <label className="inMandatory">From</label>
          <br />
          <Calendar
            value={parseDate(values.FromDate)}
            minDate={today}
            dateFormat="dd-mm-yy"
            appendTo="self"
            placeholder="Select from date.."
            className="w-100"
            onChange={(e) => {
              setField('FromDate', e.value ?? null);
              clearToDate();
            }}
          />
          {fieldError('FromDate', 'From date is required')}
        </div>
        <div className="col">
          <label className="inMandatory">To</label>
          <br />
          <Calendar
            value={parseDate(values.ToDate)}
            minDate={parseDate(values.FromDate) ?? today}
            dateFormat="dd-mm-yy"
            appendTo="self"
            placeholder="Select to date.."
            className="w-100"
            onChange={(e) => setField('ToDate', e.value ?? null)}
          />
          {fieldError('ToDate', 'To date is required')}
        </div>
      </div>

      <div className="row mt-2">
        <div className="col">
          <label className="inMandatory">Title</label>
          <InputText
            value={values.Title}
            placeholder="Enter title"
            className="form-control"
            onChange={(e) => setField('Title', e.target.value)}
          />
          {fieldError('Title', 'Title is required')}
        </div>
        <div className="col">
          <label className="inMandatory">Directorate</label>
          <Dropdown
            value={values.Directorate}
            options={options.directorates}
            optionLabel="label"
            optionValue="value"
            placeholder="Select a directorate"
            showClear={!!values.Directorate}
            appendTo="self"
            className="w-100"
            onChange={(e) => handleDirectorate(e.value ?? '')}
          />
          {fieldError('Directorate', 'Directorate is required')}
        </div>
      </div>

      <div className="row mt-2">
        <div className="col">
          <label className="inMandatory">Counselling Type</label>
          <Dropdown
            value={values.CounsellingType}
            options={options.counsellingTypes}
            optionLabel="label"
            optionValue="value"
            placeholder="Select a counselling type"
            showClear={!!values.CounsellingType}
            appendTo="self"
            className="w-100"
            onChange={(e) => handleCounsellingType(e.value ?? '')}
          />
          {fieldError('CounsellingType', 'Counselling type is required')}
        </div>
        <div className="col">
          <label className="inMandatory">Location</label>
          <Dropdown
            value={values.Location}
            options={options.locations}
            optionLabel="label"
            optionValue="value"
            placeholder="Select a location"
            disabled={locationDisable}
            showClear={!!values.Location}
            appendTo="self"
            className="w-100"
            onChange={(e) => setField('Location', e.value ?? '')}
          />
          {fieldError('Location', 'Location is required')}
        </div>
      </div>
      {values.CounsellingType !== '8' && (
        <div className="row mt-2">
          <div className="col-6">
            <label className="inMandatory">Candidate Updated By</label>
            <Dropdown
              value={values.CandidateUpdatedBy}
              options={options.vacancyLevels}
              optionLabel="label"
              optionValue="value"
              placeholder="Select a candidate updated by"
              showClear={!!values.CandidateUpdatedBy}
              appendTo="self"
              className="w-100"
              onChange={(e) => setField('CandidateUpdatedBy', e.value ?? '')}
            />
            {fieldError(
              'CandidateUpdatedBy',
              'Candidate updated by is required'
            )}
          </div>
        </div>
      )}

      <div className="row mt-4">
        <div className="col text-end">
          <Button
            type="button"
            icon="pi pi-angle-right"
            iconPos="right"
            label="Next"
            className="p-button-success"
            onClick={firstStepSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default StepDetails;
